import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { primeBillingCache } from "@/components/BillingBanner";
import { cacheSet } from "@/lib/swcache";
import { currentLang, setLang, t } from "@/lib/i18n";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import DemoLanding from "@/pages/DemoLanding";
import DistributorLanding from "@/pages/DistributorLanding";
import FeaturesPage from "@/pages/FeaturesPage";
import PricingPage from "@/pages/PricingPage";
import OnboardingPage from "@/pages/OnboardingPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import InboxPage from "@/pages/InboxPage";
import PriorityPage from "@/pages/PriorityPage";
import PriorityReport from "@/pages/PriorityReport";
import AdminPriorityPage from "@/pages/AdminPriorityPage";
import AdminCodes from "@/pages/AdminCodes";
import AdminDistributors from "@/pages/AdminDistributors";
import DistributorRegister from "@/pages/DistributorRegister";
import DistributorPanel from "@/pages/DistributorPanel";
import RefRedirect from "@/pages/RefRedirect";
import ContactsPage from "@/pages/ContactsPage";
import RulesPage from "@/pages/RulesPage";
import CalendarPage from "@/pages/CalendarPage";
import SettingsPage from "@/pages/SettingsPage";
import BillingPage from "@/pages/BillingPage";
import AdminPricing from "@/pages/AdminPricing";
import AdminEconomics from "@/pages/AdminEconomics";
import AdminSecurity from "@/pages/AdminSecurity";
import TeamPage from "@/pages/TeamPage";
import MemoryPage from "@/pages/MemoryPage";
import JoinPage from "@/pages/JoinPage";
import ComposePage from "@/pages/ComposePage";
import AdminKeys from "@/pages/AdminKeys";
import AdminUsers from "@/pages/AdminUsers";
import AdminStats from "@/pages/AdminStats";
import AdminLanguages from "@/pages/AdminLanguages";
import AdminLegal from "@/pages/AdminLegal";
import LegalPage from "@/pages/LegalPage";
import AdminWebsite from "@/pages/AdminWebsite";
import AdminProfit from "@/pages/AdminProfit";
import AdminExport from "@/pages/AdminExport";
import AdminPackages from "@/pages/AdminPackages";
import OAuthDocsPage from "@/pages/OAuthDocsPage";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function Protected({ children, allowOnboarding = false }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center text-sm text-gray-500">{t("common.loading", "Loading…")}</div>;
  if (user === false) return <Navigate to="/login" replace />;
  const stage = user.onboarding_stage || "done";
  if (!allowOnboarding && stage !== "done") return <Navigate to="/onboarding" replace />;
  if (allowOnboarding && stage === "done") return <Navigate to="/app" replace />;
  return children;
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("agi_token");
    if (!token) { setUser(false); return; }
    api.get("/auth/bootstrap").then(async (r) => {
      const d = r.data;
      if (d.user?.refreshed_token) localStorage.setItem("agi_token", d.user.refreshed_token);
      primeBillingCache(d.billing_alert, d.token_pool);
      if (d.counts) cacheSet("counts", d.counts);
      if (d.user?.ui_lang) {
        if (d.user.ui_lang !== currentLang()) await setLang(d.user.ui_lang);
      } else if (d.user && currentLang() !== "en") {
        api.post("/i18n/ui-lang", { lang: currentLang() }).catch(() => {});
      }
      setUser(d.user);
    }).catch(() => setUser(false));
  }, []);

  const login = (token, u) => { localStorage.setItem("agi_token", token); setUser(u); };
  const logout = () => { localStorage.removeItem("agi_token"); setUser(false); };
  const refreshUser = async () => {
    try { const r = await api.get("/auth/me"); setUser(r.data); return r.data; } catch { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser }}>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/demo" element={<DemoLanding />} />
          <Route path="/become-a-distributor" element={<DistributorLanding />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/app" element={<Protected><InboxPage /></Protected>} />
          <Route path="/priority" element={<Protected><PriorityPage /></Protected>} />
          <Route path="/priority/report" element={<Protected><PriorityReport /></Protected>} />
          <Route path="/onboarding" element={<Protected allowOnboarding><OnboardingPage /></Protected>} />
          <Route path="/compose" element={<Protected><ComposePage /></Protected>} />
          <Route path="/team" element={<Protected><TeamPage /></Protected>} />
          <Route path="/memory" element={<Protected><MemoryPage /></Protected>} />
          <Route path="/join/:token" element={<JoinPage />} />
          <Route path="/contacts" element={<Protected><ContactsPage /></Protected>} />
          <Route path="/rules" element={<Protected><RulesPage /></Protected>} />
          <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
          <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
          <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
          <Route path="/admin" element={<Protected><Navigate to="/admin/keys" replace /></Protected>} />
          <Route path="/admin/keys" element={<Protected><AdminKeys /></Protected>} />
          <Route path="/admin/packages" element={<Protected><AdminPackages /></Protected>} />
          <Route path="/admin/pricing" element={<Protected><AdminPricing /></Protected>} />
          <Route path="/admin/economics" element={<Protected><AdminEconomics /></Protected>} />
          <Route path="/admin/security" element={<Protected><AdminSecurity /></Protected>} />
          <Route path="/admin/users" element={<Protected><AdminUsers /></Protected>} />
          <Route path="/admin/stats" element={<Protected><AdminStats /></Protected>} />
          <Route path="/admin/priority" element={<Protected><AdminPriorityPage /></Protected>} />
          <Route path="/admin/codes" element={<Protected><AdminCodes /></Protected>} />
          <Route path="/admin/distributors" element={<Protected><AdminDistributors /></Protected>} />
          <Route path="/admin/languages" element={<Protected><AdminLanguages /></Protected>} />
          <Route path="/admin/legal" element={<Protected><AdminLegal /></Protected>} />
          <Route path="/privacy-policy" element={<LegalPage docKey="privacy" />} />
          <Route path="/terms" element={<LegalPage docKey="terms" />} />
          <Route path="/distributors" element={<DistributorRegister />} />
          <Route path="/distributors/terms" element={<LegalPage docKey="distributor-terms" />} />
          <Route path="/distributors/privacy" element={<LegalPage docKey="distributor-privacy" />} />
          <Route path="/distributor" element={<DistributorPanel />} />
          <Route path="/d/:refCode" element={<RefRedirect />} />
          <Route path="/admin/website" element={<Protected><AdminWebsite /></Protected>} />
          <Route path="/admin/profit" element={<Protected><AdminProfit /></Protected>} />
          <Route path="/admin/export" element={<Protected><AdminExport /></Protected>} />
          <Route path="/docs/oauth" element={<Protected><OAuthDocsPage /></Protected>} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
